import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore, getDisplayName } from "../store/useAuthStore";
import { authClient } from "@/lib/auth-client";
import { hasRole, isTeacher, type ValidRole } from "@/utils/role-utils";
import { setSupportSession } from "@/utils/supportSession";

export { getDisplayName };

export const useAuth = () => {
  const navigate = useNavigate();
  const {
    token,
    user,
    isAuthenticated,
    isLoading,
    error,
    setAuth,
    clearAuth,
    setError,
    setLoading,
  } = useAuthStore();

  const login = async (username: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await authClient.signIn.username({
        username,
        password,
        callbackURL: "/",
      });

      if (error) {
        // Handle email verification required (403)
        if (error.status === 403) {
          throw new Error(
            "Please verify your email address before logging in.",
          );
        }
        throw new Error(error.message || "Login failed");
      }
      if (!data?.user) {
        throw new Error("Login failed");
      }

      const user = data.user;
      // A credential sign-in is never a support session: drop any marker
      // left by a previous one before this identity is established.
      setSupportSession(null);
      setAuth(data.token || "", {
        id: parseInt(user.id) || 0,
        username: user.username || "",
        email: user.email,
        name: user.name || "",
        role: (user as any).account_role || (user as any).role || "STUDENT",
        account_type: (user as any).account_type,
        picture: user.image || undefined,
        classrooms: [],
      });

      return { success: true };
    } catch (error: any) {
      const errorMessage = error.message || "Login failed";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    await clearAuth();
    navigate("/");
  };

  const requireAuth = (redirectTo?: string) => {
    if (!isAuthenticated) {
      navigate(redirectTo || "/login");
      return false;
    }
    return true;
  };

  const requireRole = (role: ValidRole, redirectTo?: string) => {
    if (!requireAuth(redirectTo)) {
      return false;
    }

    if (!hasRole(user?.role, role)) {
      const defaultRedirect = isTeacher(user?.role)
        ? "/lessons"
        : "/classrooms";
      navigate(redirectTo || defaultRedirect);
      return false;
    }

    return true;
  };

  return useMemo(
    () => ({
      // State
      token,
      isAuthenticated,
      user,
      isLoading,
      error,

      // Actions
      login,
      logout,
      requireAuth,
      requireRole,
    }),
    [token, isAuthenticated, user, isLoading, error],
  );
};
